import { useMemo } from 'react';
import { StanDropdown } from '@/components';
import type { DropdownMenuOption, DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { type Page } from '@/types/store';
import { useDefaultStore } from '@/stores/defaultStore';
import { PRODUCT_TYPE_LABELS } from '@/lib';
import { getFunnelProductEligibility } from '../../../logic';
import { SectionHeader } from './SectionHeader';
import { SelectedProductPreview } from './SelectedProductPreview';

interface ProductPickerProps {
    pageId: number;
    sectionNumber: number;
    description: string;
    selectedPageId: number | null;
    funnelPriceOverride: number | null;
    disabled?: boolean;
    onSelect: (referencePageId: number) => void;
}

export const ProductPicker = ({
    pageId,
    sectionNumber,
    description,
    selectedPageId,
    funnelPriceOverride,
    disabled,
    onSelect,
}: ProductPickerProps) => {
    const { store: linksiteStore } = useDefaultStore();

    const eligiblePages = useMemo<Page[]>(() => {
        if (!linksiteStore?.pages) return [];
        return linksiteStore.pages.filter(p => getFunnelProductEligibility(p).eligible);
    }, [linksiteStore?.pages]);

    const productOptions: DropdownMenuOption[] = useMemo(() => {
        const grouped = new Map<string, Page[]>();
        for (const p of eligiblePages) {
            const list = grouped.get(p.type) ?? [];
            list.push(p);
            grouped.set(p.type, list);
        }

        // Preserve the order declared in PRODUCT_TYPE_LABELS so groups are consistent.
        const orderedTypes = Object.keys(PRODUCT_TYPE_LABELS).filter(t => grouped.has(t));
        for (const t of grouped.keys()) {
            if (!orderedTypes.includes(t)) orderedTypes.push(t);
        }

        const options: DropdownMenuOption[] = [];
        for (const type of orderedTypes) {
            const pages = grouped.get(type) ?? [];
            if (pages.length === 0) continue;
            const headerLabel = PRODUCT_TYPE_LABELS[type as keyof typeof PRODUCT_TYPE_LABELS] ?? type;
            options.push({
                label: headerLabel,
                value: `__header_${type}`,
                type: 'header',
            });
            for (const p of pages) {
                options.push({
                    label: p.data?.product?.title?.trim() || 'Untitled Product',
                    value: String(p.page_id),
                    icon: p.data?.product?.image || undefined,
                });
            }
        }

        return options;
    }, [eligiblePages]);

    const selectedOption = useMemo(
        () => productOptions.find(o => o.value === String(selectedPageId)),
        [productOptions, selectedPageId]
    );

    const selectedPage = useMemo(
        () => eligiblePages.find(p => p.page_id === selectedPageId) ?? null,
        [eligiblePages, selectedPageId]
    );

    const handleSelect = (_value: DropdownValue, option: DropdownMenuOption) => {
        onSelect(Number(option.value));
    };

    return (
        <div className='flex flex-col gap-4 md:gap-6'>
            <SectionHeader number={sectionNumber} title='Product' />

            <div className='flex flex-col gap-2'>
                <p className='text-body-sm text-dark'>
                    {description}{' '}
                    <a
                        href='https://help.stan.store'
                        target='_blank'
                        rel='noreferrer'
                        className='text-brand-main hover:underline'
                    >
                        Learn more
                    </a>
                </p>

                <StanDropdown
                    id={`funnel-page-product-${pageId}`}
                    name={`funnel-page-product-${pageId}`}
                    placeholder='Select...'
                    options={productOptions}
                    onSelect={handleSelect}
                    preventDeselect
                    disabled={disabled}
                    currentValue={selectedOption ?? undefined}
                />

                {productOptions.length === 0 && linksiteStore && (
                    <p className='text-body-sm text-gray'>
                        No eligible products found in your store. Create a product first, then come back to add it here.
                    </p>
                )}
            </div>

            {selectedPage && <SelectedProductPreview page={selectedPage} funnelPrice={funnelPriceOverride} />}
        </div>
    );
};
